import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  deleteProviderSecret,
  hasProviderSecret,
  loadProviderSecret,
  saveProviderSecret,
  secretUnlockMessage,
} from "@/lib/browser-store";
import type { ProviderSecret } from "@/types";
import { useWallet } from "./WalletContext";

type ProviderValue = {
  secret: ProviderSecret | null;
  hasSavedSecret: boolean;
  busy: boolean;
  save: (secret: ProviderSecret) => Promise<void>;
  unlock: () => Promise<void>;
  remove: () => Promise<void>;
};

const ProviderContext = createContext<ProviderValue | null>(null);

export function ProviderVault({ children }: { children: ReactNode }) {
  const { address, signVaultMessage } = useWallet();
  const [secret, setSecret] = useState<ProviderSecret | null>(null);
  const [hasSavedSecret, setHasSavedSecret] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setSecret(null);
    if (!address) {
      setHasSavedSecret(false);
      return () => { active = false; };
    }
    hasProviderSecret(address)
      .then((saved) => { if (active) setHasSavedSecret(saved); })
      .catch(() => { if (active) setHasSavedSecret(false); });
    return () => { active = false; };
  }, [address]);

  const signature = useCallback(async () => {
    if (!address) throw new Error("Connect your wallet first.");
    return signVaultMessage(secretUnlockMessage(address));
  }, [address, signVaultMessage]);

  const save = useCallback(
    async (nextSecret: ProviderSecret) => {
      if (!address) throw new Error("Connect your wallet first.");
      setBusy(true);
      try {
        await saveProviderSecret(address, await signature(), nextSecret);
        setSecret(nextSecret);
        setHasSavedSecret(true);
      } finally {
        setBusy(false);
      }
    },
    [address, signature],
  );

  const unlock = useCallback(async () => {
    if (!address) throw new Error("Connect your wallet first.");
    setBusy(true);
    try {
      const nextSecret = await loadProviderSecret(address, await signature());
      if (!nextSecret) throw new Error("No saved AI key was found for this wallet.");
      setSecret(nextSecret);
      setHasSavedSecret(true);
    } finally {
      setBusy(false);
    }
  }, [address, signature]);

  const remove = useCallback(async () => {
    if (!address) return;
    await deleteProviderSecret(address);
    setSecret(null);
    setHasSavedSecret(false);
  }, [address]);

  const value = useMemo(
    () => ({ secret, hasSavedSecret, busy, save, unlock, remove }),
    [secret, hasSavedSecret, busy, save, unlock, remove],
  );
  return <ProviderContext.Provider value={value}>{children}</ProviderContext.Provider>;
}

export function useProviderVault() {
  const value = useContext(ProviderContext);
  if (!value) throw new Error("useProviderVault must be used inside ProviderVault.");
  return value;
}
